import { useCallback, useState } from 'react';
import { UseMutationOptions, UseMutationResult } from 'react-query';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { ConfirmationModal, useModalControl } from '@frontend/design-system';

const PAYMENT_MENTHOD_IN_USE_ERROR = 'payment method is already in use';

export type DeleteMethodOptions = { paymentMethodId: string; locationId: string };

export interface UseDeletePaymentMethodApi {
  usePaymentPlansInvalidation: () => {
    invalidatePaymentMethodsByPerson: (patientId?: string, locationId?: string) => void;
  };
  useDeletePaymentMethod: (
    options: UseMutationOptions<unknown, unknown, DeleteMethodOptions>
  ) => UseMutationResult<unknown, unknown, DeleteMethodOptions, unknown>;
}
export type UseDeletePaymentMethodProps = {
  locationId?: string;
  personId?: string;
  api: UseDeletePaymentMethodApi;
  onSuccess?: (paymentMethod: PaymentMethod) => void;
  onError?: (message: string) => void;
};
export const useDeletePaymentMethod = ({
  locationId,
  personId,
  onSuccess,
  onError,
  api,
}: UseDeletePaymentMethodProps) => {
  const { t } = useTranslation('payments');
  const [isDeleting, setIsDeleting] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethod | null>(null);
  const deleteModalControls = useModalControl();

  const { invalidatePaymentMethodsByPerson } = api.usePaymentPlansInvalidation();

  const { mutateAsync: deletePaymentMethod } = api.useDeletePaymentMethod({
    onSuccess: () => {
      /**
       *  Developer note: A timeout before calling success methods,
       * does not seem to be necessary. If weird things are happening,
       * consider adding one around onSuccess or also invalidatePaymentMethodsByPerson.
       */
      invalidatePaymentMethodsByPerson(personId, locationId);
      setIsDeleting(false);
      if (paymentMethodToDelete && onSuccess) {
        onSuccess(paymentMethodToDelete);
      }
      setPaymentMethodToDelete(null);
    },
    onError: (error) => {
      if (error && error instanceof HttpError) {
        if (error.status === 400 && error.message.includes(PAYMENT_MENTHOD_IN_USE_ERROR)) {
          onError?.(t('This payment method cannot be deleted, it is still being used in at least one payment plan.'));
        } else {
          onError?.(error.message || t('An error occurred in delete.'));
        }
      }
      setIsDeleting(false);
    },
  });

  const showConfirmDeletePaymentModal = useCallback(
    (pm: PaymentMethod) => {
      setPaymentMethodToDelete(pm);
      deleteModalControls.openModal();
    },
    [deleteModalControls]
  );

  const onConfirmDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      await deletePaymentMethod({
        paymentMethodId: paymentMethodToDelete?.id ?? '',
        locationId: locationId ?? '',
      });
    } finally {
      deleteModalControls.closeModal();
    }
  }, [deletePaymentMethod, paymentMethodToDelete, locationId, deleteModalControls]);

  const resetPaymentMethodToDelete = useCallback(() => {
    setPaymentMethodToDelete(null);
  }, []);

  const onCloseConfirmationModal = useCallback(() => {
    deleteModalControls.closeModal();
    resetPaymentMethodToDelete();
  }, [deleteModalControls, resetPaymentMethodToDelete]);

  const ConFirmDeleteModal = (
    <ConfirmationModal
      {...deleteModalControls.modalProps}
      title={t('Delete Card')}
      message={t('Are you sure you want to delete this Card?')}
      confirmLabel={t('Delete')}
      onConfirm={onConfirmDelete}
      onCancel={onCloseConfirmationModal}
      destructive
    />
  );

  return {
    isDeleting,
    ConFirmDeleteModal,
    showConfirmDeletePaymentModal,
    resetPaymentMethodToDelete,
  };
};
